import { Fragment, useLayoutEffect, useRef, useState } from "react";
import QuestionLayout from "./QuestionLayout";
import {
  TREBLE_CLEF_PATH,
  BASS_CLEF_PATH
} from "./staff/clefPaths";
import { pitchToY } from "./staff/pitchMath";
import { Glyph } from "./staff/glyphs.jsx";

const STAFF_W = 150;
const STAFF_H = 72;

const MINI_STAFF_X1 = 16;
const MINI_STAFF_X2 = 136;
const MINI_STAFF_TOP = 18;
const LINE_GAP = 10;

const MINI_CLEF_SCALE = 0.038;
const MINI_CLEF_X = 34;   // move clef left 8 px
const KEY_SIG_X = 66;     // move key signature left 12 px

const TREBLE_SHARP_Y = ["F#5", "C#5", "G#5", "D#5", "A#4", "E#5", "B#4"];
const TREBLE_FLAT_Y = ["Bb4", "Eb5", "Ab4", "Db5", "Gb4", "Cb5", "Fb4"];

const BASS_SHARP_Y = ["F#3", "C#3", "G#3", "D#3", "A#2", "E#3", "B#2"];
const BASS_FLAT_Y = ["Bb2", "Eb3", "Ab2", "Db3", "Gb2", "Cb3", "Fb2"];

function miniPitchToY(pitch, clef) {
  return pitchToY(pitch, clef) - (40 - MINI_STAFF_TOP);
}

function renderMiniStaffLines() {
  return (
    <g>
      {Array.from({ length: 5 }).map((_, i) => (
        <line
          key={i}
          x1={MINI_STAFF_X1}
          x2={MINI_STAFF_X2}
          y1={MINI_STAFF_TOP + i * LINE_GAP}
          y2={MINI_STAFF_TOP + i * LINE_GAP}
          stroke="#000"
        />
      ))}
    </g>
  );
}

function renderMiniClef(clef) {
  let path;
  let anchorY;

  if (clef === "treble") {
    path = TREBLE_CLEF_PATH;
    anchorY = MINI_STAFF_TOP + 3 * LINE_GAP;
  } else if (clef === "bass") {
    path = BASS_CLEF_PATH;
    anchorY = MINI_STAFF_TOP + LINE_GAP;
  } else {
    return null;
  }

  return (
    <g
      transform={`
        translate(${MINI_CLEF_X}, ${anchorY})
        scale(${MINI_CLEF_SCALE}, -${MINI_CLEF_SCALE})
        translate(-200, 0)
      `}
      fill="black"
    >
      <path d={path} />
    </g>
  );
}

function getKeySignaturePitches(type, clef) {
  if (clef === "bass") {
    return type === "sharps" ? BASS_SHARP_Y : BASS_FLAT_Y;
  }

  return type === "sharps" ? TREBLE_SHARP_Y : TREBLE_FLAT_Y;
}

function renderKeySignature({ type, count }, clef, baseX) {
  if (!type || !count) return null;

  const pitches = getKeySignaturePitches(type, clef);

  return pitches.slice(0, count).map((pitch, i) => {
    const accidental = pitch.includes("#") ? "#" : "b";

    return (
      <g
        key={`${pitch}-${i}`}
        transform={`translate(${baseX + i * 10}, ${miniPitchToY(pitch, clef)})`}
      >
        <Glyph symbol="quarter_note" accidental={accidental} accidentalOnly />
      </g>
    );
  });
}

function KeySigPreview({ item }) {
  if (!item) return null;

  return (
    <svg
      width={STAFF_W}
      height={STAFF_H}
      viewBox={`0 0 ${STAFF_W} ${STAFF_H}`}
      style={{ display: "block" }}
    >
      {renderMiniStaffLines()}
      {renderMiniClef(item.clef)}
      {renderKeySignature(item, item.clef, KEY_SIG_X)}
    </svg>
  );
}

function anchorPoint(el, container, side) {
  const a = el.getBoundingClientRect();
  const b = container.getBoundingClientRect();

  return {
    x:
      side === "left"
        ? a.left - b.left
        : side === "right"
        ? a.right - b.left
        : a.left - b.left + a.width / 2,
    y: a.top - b.top + a.height / 2
  };
}

export default function KeySignatureMatchWidget({
  config,
  value = {},
  onChange,
  mode = "edit",
  feedback
}) {
  const containerRef = useRef(null);
  const refs = useRef({});
  const [selectedCenter, setSelectedCenter] = useState(null);
  const [, forceRender] = useState(0);

  const leftItems = config.left || [];
  const centerItems = config.center || [];
  const rightItems = config.right || [];

  function setRef(id, el) {
    if (el) refs.current[id] = el;
    else delete refs.current[id];
  }

  function getPartFeedback(centerId, side) {
    return feedback?.parts?.[`${centerId}.${side}`] ?? null;
  }

  function updateConnection(centerId, side, targetId) {
    if (mode !== "edit") return;

    onChange({
      ...value,
      [centerId]: {
        ...(value[centerId] || {}),
        [side]: targetId
      }
    });
  }

  function handleCenterClick(centerId) {
    if (mode !== "edit") return;
    setSelectedCenter(centerId);
  }

  function handleTargetClick(side, targetId) {
    if (mode !== "edit" || !selectedCenter) return;
    updateConnection(selectedCenter, side, targetId);
  }

  useLayoutEffect(() => {
    forceRender((n) => n + 1);

    function handleResize() {
      forceRender((n) => n + 1);
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const lines = [];

  if (containerRef.current) {
    centerItems.forEach((center) => {
      const conn = value[center.id] || {};
      const centerEl = refs.current[center.id];

      if (!centerEl) return;

      if (conn.left && refs.current[conn.left]) {
        lines.push({
          id: `${center.id}-left`,
          a: anchorPoint(centerEl, containerRef.current, "left"),
          b: anchorPoint(refs.current[conn.left], containerRef.current, "right"),
          feedback: getPartFeedback(center.id, "left")
        });
      }

      if (conn.right && refs.current[conn.right]) {
        lines.push({
          id: `${center.id}-right`,
          a: anchorPoint(centerEl, containerRef.current, "right"),
          b: anchorPoint(refs.current[conn.right], containerRef.current, "left"),
          feedback: getPartFeedback(center.id, "right")
        });
      }
    });
  }

  const keySigButtonStyle = {
    position: "relative",
    zIndex: 1,
    width: 166,
    height: 88,
    border: "1px solid #aaa",
    background: "white",
    borderRadius: 8,
    padding: 0,
    cursor: mode === "edit" ? "pointer" : "default",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  };

  return (
    <QuestionLayout
      number={config.number}
      prompt={config.prompt}
      points={config.points}
      mode={mode}
      feedback={feedback}
    >
      <div
        ref={containerRef}
        style={{
          position: "relative",
          display: "grid",
          gridTemplateColumns: "166px 130px 166px",
          columnGap: 42,
          rowGap: 18,
          alignItems: "center",
          justifyContent: "center",
          padding: "12px 0"
        }}
      >
        <svg
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            pointerEvents: "none",
            overflow: "visible",
            zIndex: 0
          }}
        >
          {lines.map((line) => {
            const fb = line.feedback;

            return (
              <line
                key={line.id}
                x1={line.a.x}
                y1={line.a.y}
                x2={line.b.x}
                y2={line.b.y}
                stroke={
                  mode === "review"
                    ? fb?.correct
                      ? "#2e7d32"
                      : "#c62828"
                    : "#222"
                }
                strokeWidth={3}
              />
            );
          })}
        </svg>

        {centerItems.map((center, i) => {
          const left = leftItems[i];
          const right = rightItems[i];

          if (!left || !right) return null;

          return (
            <Fragment key={center.id}>
              <button
                type="button"
                ref={(el) => setRef(left.id, el)}
                disabled={mode !== "edit"}
                onClick={() => handleTargetClick("left", left.id)}
                style={keySigButtonStyle}
              >
                <KeySigPreview item={left} />
              </button>

              <button
                type="button"
                ref={(el) => setRef(center.id, el)}
                disabled={mode !== "edit"}
                onClick={() => handleCenterClick(center.id)}
                style={{
                  position: "relative",
                  zIndex: 1,
                  fontSize: 17,
                  fontWeight: "bold",
                  padding: "12px 14px",
                  borderRadius: 8,
                  border:
                    selectedCenter === center.id
                      ? "2px solid #1976d2"
                      : "1px solid #999",
                  background: "white",
                  cursor: mode === "edit" ? "pointer" : "default"
                }}
              >
                {center.label}
              </button>

              <button
                type="button"
                ref={(el) => setRef(right.id, el)}
                disabled={mode !== "edit"}
                onClick={() => handleTargetClick("right", right.id)}
                style={keySigButtonStyle}
              >
                <KeySigPreview item={right} />
              </button>
            </Fragment>
          );
        })}
      </div>
    </QuestionLayout>
  );
}